import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getScheduleBlockClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import type {
  CreateScheduleBlockRequest,
  UpdateScheduleBlockRequest,
  CheckOverlapsRequest,
} from '@vagusx/clients';

export function useScheduleBlocks(options?: {
  providerId?: string;
  fromDate?: string;
  toDate?: string;
}) {
  return useQuery({
    queryKey: queryKeys.scheduleBlocks.list(options),
    queryFn: () => getScheduleBlockClient().list(options),
    select: (data) => data.blocks,
  });
}

export function useScheduleBlock(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.scheduleBlocks.detail(id ?? ''),
    queryFn: () => getScheduleBlockClient().getById(id!),
    enabled: !!id,
  });
}

export function useCheckOverlaps() {
  return useMutation({
    mutationFn: (data: CheckOverlapsRequest) => getScheduleBlockClient().checkOverlaps(data),
  });
}

export function useCreateScheduleBlock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateScheduleBlockRequest) => getScheduleBlockClient().create(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.scheduleBlocks.all });
      if (result.affectedAppointmentCount > 0) {
        queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all });
        toast.success(
          `Block created. ${result.affectedAppointmentCount} appointment(s) marked for rescheduling.`
        );
      } else {
        toast.success('Schedule block created');
      }
    },
  });
}

export function useUpdateScheduleBlock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateScheduleBlockRequest }) =>
      getScheduleBlockClient().update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.scheduleBlocks.all });
      toast.success('Schedule block updated');
    },
  });
}

export function useDeleteScheduleBlock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getScheduleBlockClient().remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.scheduleBlocks.all });
      toast.success('Schedule block deleted');
    },
  });
}
